import { useCallback } from 'react'
import { toast } from 'sonner'
import {
  type ShapeTextFrameHandle,
  getShapeTextFrame,
} from '@/components/app/shapes/shape-text-frame-registry'
import type { Paragraph } from '@/api/types/stowed-element'
import { defaultShapeTextParagraphs } from '@/lib/shapes/shape-text-frame'
import {
  CHAIN_LOCKED_MEMBER_EDIT_BLOCKED_MESSAGE,
  canEditChainFrameContent,
} from '@/lib/text-linking/chain-content-edit-policy'
import { getShapeById } from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { ShapeTextCommitApi } from './use-shape-text-commit'
import type { ChainTextSelectionApi } from './use-chain-text-selection'

export function useChainTextClipboard(
  ctx: ShapeManagerStateApi & ShapeUpdatesApi & ShapeTextCommitApi & ChainTextSelectionApi
) {
  const {
    cancelShapeTextCommit,
    clearChainTextSelection,
    cloneShapeTextParagraphs,
    editStartShapeTextParagraphsRef,
    editingShapeTextIdRef,
    onShapeCommitted,
    reanchorChainTextSelection,
    resolveChainSelectionParts,
    shapesRef,
    updateShape,
  } = ctx

  /** Concatenated styled HTML + plain text for a cross-frame selection, in flow order. */
  const readChainSelectionClipboard = useCallback((): { html: string; plain: string } | null => {
    const parts = resolveChainSelectionParts()

    if (!parts) return null

    let html = ''
    let plain = ''

    for (const part of parts) {
      const handle = getShapeTextFrame(part.frameId)

      if (!handle) continue
      html += handle.buildStyledHtmlForRange(part.start, part.end)
      plain += handle.getPlainText().slice(part.start, part.end)
    }

    return plain ? { html, plain } : null
  }, [resolveChainSelectionParts])

  /**
   * Write a cross-frame selection to the clipboard. Returns false when there is no cross-frame
   * selection, so callers fall through to the single-frame path unchanged.
   */
  const execChainTextCopy = useCallback((): boolean => {
    const payload = readChainSelectionClipboard()

    if (!payload) return false

    void navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([payload.plain], { type: 'text/plain' }),
        'text/html': new Blob([payload.html], { type: 'text/html' }),
      }),
    ])

    return true
  }, [readChainSelectionClipboard])

  /**
   * Apply one edit across every frame a cross-frame selection touches, as a single history
   * transaction — three frame writes plus the reflow they trigger are one user action and must
   * undo as one.
   *
   * Committed last-frame-first so the head's commit lands last: `handleShapeCommitted` reflows the
   * chain from each committed frame, and running the head's reflow after every member is already
   * trimmed is the only ordering that settles in one pass. The intermediate reflows are harmless
   * because reflow is idempotent (WMPCM-9199 — see `reflowAndPersistChain`).
   */
  const mutateChainTextSelection = useCallback(
    (
      apply: (
        handle: ShapeTextFrameHandle,
        part: { frameId: string; start: number; end: number },
        index: number
      ) => { before: Paragraph[]; after: Paragraph[] } | null,
      /**
       * What happens to the selection afterwards.
       *
       * `'collapse'` for an edit that consumed the range — replacing a selection with text leaves
       * a caret, so there is nothing left to keep. `'reanchor'` for one that only restyled it: the
       * same characters are still selected, so the range survives and re-resolves against the new
       * per-frame distribution (WMPCM-9200). Without the re-anchor a size change would silently
       * drop the selection, forcing the user to re-select across frames before each next change.
       */
      after: 'collapse' | 'reanchor' = 'collapse'
    ): boolean => {
      const parts = resolveChainSelectionParts()

      if (!parts) return false

      // A locked member is part of the story and the selection spans it (WMPCM-9728 — the Ctrl+A
      // seeder deliberately no longer filters it out), but a USER-INITIATED content edit must not
      // rewrite it. Which way that is enforced depends on whether the edit changes the length,
      // and `after` already carries exactly that distinction:
      //
      // - `'collapse'` — cut, Delete/Backspace, type-over. REFUSE the whole edit. Deleting only
      //   the unlocked members is not a partial success: shortening the story sets
      //   unconditional under D4, so the follow-on redistribution would reclaim the locked
      //   frame's paragraphs into the frame upstream of it. That relocates its text rather than
      //   protecting it — worse than refusing. Same call as `home.tsx`'s Backspace-at-frame-start,
      //   which refuses to delete from a locked predecessor.
      // - `'reanchor'` — a restyle. SKIP the locked member and carry on, exactly as the
      //   whole-frame style path in `home.tsx` does: no length changes, so nothing reflows, and
      //   refusing here would instead fall through to `applyShapeTextRunStyle`'s single-frame
      //   branch and silently narrow a chain-wide change to the caret's frame.
      const isLockedPart = (part: { frameId: string }): boolean =>
        !canEditChainFrameContent({
          locked: getShapeById(shapesRef.current, part.frameId)?.locked,
        })

      if (after === 'collapse' && parts.some(isLockedPart)) {
        // Stable id: the refusal returns before `clearChainTextSelection()`, so the selection
        // survives and a held-down Backspace re-fires this on every key repeat. Without it
        // they stack.
        toast.error(CHAIN_LOCKED_MEMBER_EDIT_BLOCKED_MESSAGE, { id: 'chain-locked-edit' })

        return false
      }

      // The debounced commit would otherwise fire afterwards against a baseline this edit has
      // already invalidated.
      cancelShapeTextCommit()

      const historyTxnId = `chain-text-edit:${crypto.randomUUID()}`
      let changed = false

      for (let i = parts.length - 1; i >= 0; i--) {
        const part = parts[i]!
        const handle = getShapeTextFrame(part.frameId)

        if (!handle) continue
        // Only reachable on the `'reanchor'` (restyle) path — `'collapse'` already refused above
        // if any part was locked.
        if (isLockedPart(part)) continue

        const result = apply(handle, part, i)

        if (!result) continue
        changed = true
        // `bypassEditGuards` because `shouldBlockShapeUpdate` refuses a paragraph write on a
        // FROZEN member, and freeze is placement-geometry-only, so it must not block one
        // (WMPCM-6972/7052 — the same reason chain reflow bypasses them). It is not licence to
        // rewrite a LOCKED member: those are filtered out above.
        updateShape(part.frameId, { paragraphs: result.after }, { bypassEditGuards: true })

        const latest = getShapeById(shapesRef.current, part.frameId)

        if (latest) {
          onShapeCommitted?.(latest, 'update', {
            before: { paragraphs: result.before },
            after: { paragraphs: result.after },
            historyTxnId,
          })
        }
      }

      if (changed) {
        // Re-baseline the editing frame: its content changed underneath the edit session, and the
        // next debounced commit must diff against the new content, not the pre-edit one.
        const editingId = editingShapeTextIdRef.current

        if (editingId) {
          const settled = getShapeById(shapesRef.current, editingId)

          editStartShapeTextParagraphsRef.current = cloneShapeTextParagraphs(
            settled?.paragraphs ?? defaultShapeTextParagraphs()
          )
        }

        if (after === 'collapse') clearChainTextSelection()
        else reanchorChainTextSelection()
      }

      return changed
    },
    [
      cancelShapeTextCommit,
      clearChainTextSelection,
      cloneShapeTextParagraphs,
      onShapeCommitted,
      reanchorChainTextSelection,
      resolveChainSelectionParts,
      updateShape,
      editStartShapeTextParagraphsRef,
      editingShapeTextIdRef,
      shapesRef,
    ]
  )

  /**
   * Cut or copy a live cross-frame selection. Returns false when there is no such selection, so
   * every caller can fall back to the single-textarea path.
   *
   * Handed to `ShapeTextEditor` as a prop as well as exposed on the ref, because the two entry
   * points are genuinely separate: the textarea's `onKeyDown` calls `stopPropagation`, so a
   * keyboard Ctrl+X/Ctrl+C never reaches the canvas handler that owns the ref and could only ever
   * hit `execCut`/`execCopy`. A live cross-frame selection collapses the textarea at the anchor,
   * so that path saw an empty selection and did nothing — keyboard cut/copy across frames was a
   * silent no-op, working only from the Edit and context menus (WMPCM-9396).
   */
  const execChainClipboard = useCallback(
    (action: 'copy' | 'cut'): boolean => {
      if (!execChainTextCopy()) return false

      if (action === 'cut') {
        mutateChainTextSelection((handle, part) => handle.replaceRange(part.start, part.end, ''))
      }

      return true
    },
    [execChainTextCopy, mutateChainTextSelection]
  )

  /**
   * Replace an entire cross-frame selection with `text` — Backspace/Delete pass `''`, a printable
   * keystroke passes the character. The text lands in the FIRST covered frame (flow order), which
   * is where the selection starts and therefore where the caret belongs after the replacement;
   * every other covered frame just loses its span.
   *
   * Returns whether the replacement happened, so a caller that has more work to do after clearing
   * the range (`applyClipboardPayload`'s paste) can abort when it is refused for spanning a locked
   * member (WMPCM-9728) instead of inserting on top of a range that is still there.
   */
  const replaceChainTextSelection = useCallback(
    (text: string): boolean =>
      mutateChainTextSelection((handle, part, index) =>
        handle.replaceRange(part.start, part.end, index === 0 ? text : '')
      ),
    [mutateChainTextSelection]
  )

  return {
    readChainSelectionClipboard,
    execChainTextCopy,
    mutateChainTextSelection,
    execChainClipboard,
    replaceChainTextSelection,
  }
}

export type ChainTextClipboardApi = ReturnType<typeof useChainTextClipboard>
